/**
 * DEV-ONLY Laravel API request tracer for the 429 investigation.
 *
 * Logs method, path, status, sequence, and rate-limit headers.
 * Does not log tokens, cookies, bodies, or personal data.
 *
 * Remove this file and its imports after the investigation.
 */

let seq = 0

function pathOnly(url: string): string {
  try {
    if (/^https?:\/\//i.test(url)) {
      const parsed = new URL(url)
      return `${parsed.pathname}${parsed.search}`
    }
  }
  catch {
    // Fall through.
  }
  return url
}

function stackInitiator(): string {
  const stack = new Error().stack ?? ''
  for (const raw of stack.split('\n')) {
    const line = raw.trim()
    if (!line.includes('app/') && !line.includes('app\\')) continue
    if (line.includes('apiTrace')) continue
    if (line.includes('useApi.')) continue
    const match = line.match(/app[\\/][^\s:?)]+/)
    if (match) return match[0].replaceAll('\\', '/')
  }
  return ''
}

function headerGet(headers: unknown, name: string): string | null {
  if (!headers) return null
  const h = headers as { get?: (key: string) => string | null }
  if (typeof h.get === 'function') return h.get(name)
  const rec = headers as Record<string, string | undefined>
  return rec[name] ?? rec[name.toLowerCase()] ?? null
}

export function traceLaravelApi(
  method: string,
  url: string,
  status: number,
  initiator = '',
  headers?: unknown,
): void {
  if (!import.meta.dev) return
  seq += 1
  const time = new Date().toLocaleTimeString('en-GB', { hour12: false })
  const limit = headerGet(headers, 'X-RateLimit-Limit')
  const remaining = headerGet(headers, 'X-RateLimit-Remaining')
  const retryAfter = headerGet(headers, 'Retry-After')
  const parts = [
    `[API TRACE #${seq}]`,
    time,
    method.toUpperCase(),
    pathOnly(url),
    `→ ${status}`,
  ]
  if (limit != null) parts.push(`limit=${limit} remaining=${remaining ?? '?'}`)
  if (retryAfter != null) parts.push(`retry-after=${retryAfter}`)
  const who = initiator || stackInitiator()
  if (who) parts.push(`@ ${who}`)
  // eslint-disable-next-line no-console
  console.debug(parts.join(' '))
}

export function captureApiInitiator(): string {
  if (!import.meta.dev) return ''
  return stackInitiator()
}
